<template>
  <Teleport to="body">
    <div
      class="pointer-events-none fixed bottom-4 right-4 z-[999999] flex items-center gap-2 rounded-lg px-3 py-2 shadow-lg transition-all duration-300"
      :class="statusClass"
      role="status"
      aria-live="polite"
    >
      <div
        class="h-2 w-2 shrink-0 rounded-full"
        :class="dotClass"
      />

      <span class="whitespace-nowrap text-xs font-medium">
        {{ statusText }}
      </span>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'

interface Props {
  isConnected: boolean
  isReconnecting?: boolean
  reconnectAttempts?: number
  maxReconnectAttempts?: number
  reconnectDelay?: number
}

const props = withDefaults(defineProps<Props>(), {
  isReconnecting: false,
  reconnectAttempts: 0,
  maxReconnectAttempts: 10,
  reconnectDelay: 0,
})

const remainingTime = ref(0)
let intervalId: ReturnType<typeof setInterval> | null = null

const stopCountdown = () => {
  if (intervalId !== null) {
    clearInterval(intervalId)
    intervalId = null
  }
}

const startCountdown = () => {
  stopCountdown()

  intervalId = setInterval(() => {
    if (remainingTime.value > 100) {
      remainingTime.value -= 100
      return
    }

    remainingTime.value = 0
    stopCountdown()
  }, 100)
}

watch(
  [() => props.reconnectDelay, () => props.isReconnecting],
  ([newDelay, isReconnecting]) => {
    if (isReconnecting && newDelay > 0) {
      remainingTime.value = newDelay
      startCountdown()
      return
    }

    remainingTime.value = 0
    stopCountdown()
  },
  { immediate: true },
)

onUnmounted(stopCountdown)

const statusClass = computed(() => {
  if (props.isConnected) {
    return [
      'bg-green-100 text-green-800',
      'dark:bg-green-900 dark:text-green-200',
    ]
  }

  if (props.isReconnecting) {
    return [
      'bg-yellow-100 text-yellow-800',
      'dark:bg-yellow-900 dark:text-yellow-200',
    ]
  }

  return [
    'bg-red-100 text-red-800',
    'dark:bg-red-900 dark:text-red-200',
  ]
})

const dotClass = computed(() => {
  if (props.isConnected) {
    return 'bg-green-500 animate-pulse'
  }

  if (props.isReconnecting) {
    return 'bg-yellow-500 animate-pulse'
  }

  return 'bg-red-500'
})

const statusText = computed(() => {
  if (props.isConnected) {
    return 'Conectado'
  }

  if (props.isReconnecting) {
    const delaySeconds = Math.ceil(remainingTime.value / 1000)

    if (delaySeconds > 0) {
      return `Reconectando en ${delaySeconds}s... (intento ${props.reconnectAttempts}/${props.maxReconnectAttempts})`
    }

    return `Reconectando... (intento ${props.reconnectAttempts}/${props.maxReconnectAttempts})`
  }

  return 'Desconectado, reintentando...'
})
</script>